import { router } from 'expo-router';
import { BranchParams } from 'react-native-branch';

import { convertQueryStringToObject } from 'navigation/utils';
import { setUTMParams, setUTMParamsFromUrl } from 'state/amplitude';
import { IUtmParams } from 'state/amplitude/types';
import { appUriScheme } from 'utils/environment';
import { logger } from 'utils/logger';

export { categorizeTraffic } from './categorize-traffic';

const getPathFromUrl = (url: string): string => {
  // Handle URI deeplinks like burgerking://menu
  if (url.includes(appUriScheme())) {
    return getPathFromAppURI(url);
  }

  // Handle full URLs from Android App Links and iOS Universal Links like https://www.bk.com/menu
  return getPathFromWebsiteUrl(url);
};

// Returns the value from the website uri
const getPathFromWebsiteUrl = (uri: string): string => {
  try {
    const url = new URL(uri);
    return `${url.pathname}${url.search}`;
  } catch {
    return '';
  }
};

// Returns the path for app scheme like burgerking://some-path
const getPathFromAppURI = (url: string): string => {
  const items = url.split(appUriScheme());
  if (items.length > 1) {
    const path = items[1];
    return `/${path}`;
  }

  return '';
};

const setUTMParameters = (deepLinkPath: string, params: BranchParams) => {
  // it can happen that utm parameters come along the utm path
  // this happens for legacy urls.
  const {
    utm_source = '',
    utm_campaign = '',
    utm_medium = '',
    utm_content = '',
    utm_term = '',
  } = convertQueryStringToObject<IUtmParams & Record<string, unknown>>(deepLinkPath);

  setUTMParams({
    utm_campaign: params['~campaign'] || utm_campaign,
    utm_medium: params['~feature'] || utm_medium,
    utm_source: params['~channel'] || utm_source,
    utm_content,
    utm_term,
  });
};

export const getPath = (params: BranchParams): string | null => {
  if (typeof params?.['+non_branch_link'] === 'string') {
    // This logic handles opening the app from non branch sources
    // like opening directly from burgerking:// or www.bk.com
    // It does not contain an app.link or alternate.link
    setUTMParamsFromUrl(params['+non_branch_link']);
    return getPathFromUrl(params['+non_branch_link']);
  }

  if (typeof params?.$deeplink_path === 'string') {
    // If utm parameters was configured in branch
    // they must appear under ~campaign, ~channel and ~feature
    // We set the utm parameters and then return the path to the
    // deeplink
    setUTMParameters(params.$deeplink_path, params);
    return params.$deeplink_path.startsWith('/')
      ? params.$deeplink_path
      : `/${params.$deeplink_path}`;
  }

  // Canonical url should be in the format of a url complete
  // like www.bk.com?query_parameters
  if (typeof params?.$canonical_url === 'string') {
    setUTMParamsFromUrl(params?.$canonical_url);
    return getPathFromWebsiteUrl(params?.$canonical_url);
  }

  return null;
};

/**
 * Ensures expo-router receives a usable path. Empty or whitespace-only paths become "/".
 * Collapses repeated leading slashes (e.g. from malformed app URIs) to a single "/".
 */
export const normalizeDeeplinkNavigationPath = (path: string | null): string => {
  if (path == null) {
    return '/';
  }
  const trimmed = path.trim();
  if (trimmed === '') {
    return '/';
  }
  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const collapsed = withLeadingSlash.replace(/^\/+/u, '/');
  return collapsed === '' ? '/' : collapsed;
};

export const isBranchUrl = (url: string): boolean => {
  return (
    url.includes('branch') ||
    url.includes('app.link') ||
    url.includes('alternate.link') ||
    url.includes('_branch_referrer')
  );
};

export const isBranchLink = (params: BranchParams): boolean => {
  return (
    typeof params?.['+non_branch_link'] === 'string' && isBranchUrl(params['+non_branch_link'])
  );
};

export const handleDeeplinks = async (params: BranchParams) => {
  // logic will attempt to get the path, but if url does not match then it
  // will redirect to home.
  const path = normalizeDeeplinkNavigationPath(getPath(params));
  setTimeout(() => {
    try {
      router.navigate(path);
    } catch (error) {
      logger.error({
        message: `Failed to navigate to path: ${path}`,
        error,
      });
    }
  }, 500);
};
